import React from 'react';
import { Tooltip, type TableColumnsType } from 'antd';
import moment from 'moment-timezone';
import { Operations } from './Operations';
import { DataSource, isAdmin, parseRawCategory } from '../../utils';
import * as Styled from './styled';
import * as Types from '../../types';

interface PermissionParams {
  user: Types.User;
  savedFilter: Types.SavedFilter;
  dataSource: DataSource;
  disableCreateFilter?: boolean;
}
export const checkCanEdit = (params: PermissionParams) => {
  const { user, savedFilter, dataSource } = params;
  // for Video & ads, pre defined filter can't be edited
  if (dataSource !== DataSource.app && savedFilter.creator === 'Pre-Defined') return false;

  // Admin user can edit any filter except for pre defined filter
  if (isAdmin(user.role)) return true;
  return user.username === savedFilter.creator;
};

export const checkCanEnable = (params: PermissionParams) => {
  const { savedFilter, dataSource } = params;
  // As of now App don't have disabled filter
  if (dataSource === DataSource.app) return false;

  return !savedFilter.enabled;
};

export const checkCanDelete = (params: PermissionParams) => {
  const { user, savedFilter, dataSource } = params;
  // for Video & ads, pre defined filter can't be deleted
  if (dataSource !== DataSource.app && savedFilter.creator === 'Pre-Defined') return false;

  if (isAdmin(user.role)) return true;
  return user.username === savedFilter.creator;
};

export const checkCanClone = (params: PermissionParams) => {
  const { disableCreateFilter, savedFilter } = params;
  // Filter with empty rule can't not be cloned
  if (!savedFilter.rule?.length) return false;

  if (disableCreateFilter) return false;
  return true;
};

export const convertToFilterInfo = (
  savedFilter: Types.SavedFilter,
): {
  id: number;
  filter: Types.Filter;
} => {
  const { fid, name, rule, hidden } = savedFilter;
  const { category, subcategory } = parseRawCategory(savedFilter.category);
  return {
    id: fid,
    filter: {
      name,
      rule,
      category,
      subcategory,
      hidden,
    },
  };
};

export const checkIsDisabledRow = (savedFilter: Types.SavedFilter, dataSource: DataSource) => {
  return !savedFilter.enabled && [DataSource.video, DataSource.ads].includes(dataSource);
};

export const checkIsFilterMatched = (filter: Types.SavedFilter, text?: string) => {
  if (!text) return true;

  const textStr = String(text).toLocaleLowerCase();
  return (
    String(filter.name).toLocaleLowerCase().includes(textStr) ||
    String(filter.creator).toLocaleLowerCase().includes(textStr) ||
    String(filter.rule_text).toLocaleLowerCase().includes(textStr) ||
    String(filter.fid).toLocaleLowerCase().includes(textStr)
  );
};

const isInternalUser = (user: Types.User) => !!user.hidden;

interface Options {
  dataSource: DataSource;
  user: Types.User;
  readonly?: boolean;
}
export const getColumns = ({ readonly, user }: Options) =>
  [
    {
      title: 'ID',
      dataIndex: 'fid',
      width: 120,
      render: (value) => {
        return <Styled.Text>{value}</Styled.Text>;
      },
      sorter: (a, b) => a.fid - b.fid,
    },
    {
      title: 'Filter Name',
      dataIndex: 'name',
      width: 220,
      render: (value) => {
        return (
          <Styled.Text maxWidth="300px">
            <Tooltip placement="topLeft" title={value}>
              {value}
            </Tooltip>
          </Styled.Text>
        );
      },
      sorter: (a, b) => String(a.name).localeCompare(String(b.name)),
    },
    {
      title: 'Rules',
      dataIndex: 'rule_text',
      sorter: (a, b) => String(a.rule_text).localeCompare(String(b.rule_text)),
      render: (value) => {
        return (
          <Styled.Text maxWidth="500px">
            <Tooltip placement="topLeft" title={value}>
              {value}
            </Tooltip>
          </Styled.Text>
        );
      },
    },
    isInternalUser(user) && {
      title: 'Hidden',
      dataIndex: 'hidden',
      width: 80,
      sorter: (a, b) => String(a.hidden ?? false).localeCompare(String(b.hidden ?? false)),
      render: (value) => {
        const text = value ? 'Yes' : 'No';
        const title = value
          ? 'The filter is only available to Conviva users'
          : 'The filter is available to all users';
        return (
          <Tooltip placement="topLeft" title={title}>
            <Styled.Text>{text}</Styled.Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Created On',
      dataIndex: 'created',
      defaultSortOrder: 'descend',
      width: 140,
      render: (value) => {
        const format = 'MMM Do YYYY';
        const text = moment(value * 1000).format(format);
        return <Styled.Text>{text}</Styled.Text>;
      },
      sorter: (a, b) => {
        const aTime = moment(a.created * 1000).valueOf() || 0;
        const bTime = moment(b.created * 1000).valueOf() || 0;
        return aTime - bTime;
      },
    },
    {
      title: 'Created By',
      dataIndex: 'creator',
      width: 160,
      render: (value) => <Styled.Text>{value}</Styled.Text>,
      sorter: (a, b) => String(a.creator).localeCompare(String(b.creator)),
    },
    !readonly && {
      title: 'Action',
      dataIndex: '$action',
      width: 80,
      render: (_, savedFilter) => <Operations savedFilter={savedFilter}></Operations>,
    },
  ].filter(Boolean) as TableColumnsType<Types.SavedFilter>;
